
import type {
  Identifiable,
  QueryRanking,
  QueryRankingElementRankingDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { RegularFilter, ToolsMessages } from '../../models'
import type { QueryRankingDomainService } from '../../services/interfaces'
import type { EpbSelectOption } from '../shared/epb-select'
import type { BaseQueryRankingModalAcceptEventPayload } from './base-query-ranking-modal.types'
import type { AcceptEventPayload } from './use-query-ranking-modal'
import { computed, defineComponent, ref } from 'vue'
import { useInstance } from '../../composables/use-instance'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import FieldValueSelect from '../shared/field-value-select.component.vue'
import BaseQueryRankingModal from './base-query-ranking-modal.component.vue'
import { useQueryRankingModal } from './use-query-ranking-modal'

type AttributesQueryRankingModalAcceptEventPayload = QueryRankingElementRankingDTO[]

export default defineComponent({
  name: 'AttributesQueryRankingModal',
  components: {
    BaseQueryRankingModal,
    FieldValueSelect,
  },
  props: {
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    queryRanking: {
      type: Object as PropType<QueryRanking>,
      required: true,
    },
    queryRankingConfig: {
      type: Object as PropType<QueryRankingElementRankingDTO & Identifiable>,
      required: false,
      default: null,
    },
    pivots: {
      type: Object as PropType<Record<string, RegularFilter>>,
      default: () => ({}),
    },
  },
  emits: {
    close: () => {},
    accept: (payload: AttributesQueryRankingModalAcceptEventPayload) => {},
  },
  setup(props, ctx: SetupContext) {
    const { rankableFieldConfigs } = useInstance()

    const fieldNameOptions = computed((): EpbSelectOption[] => {
      return rankableFieldConfigs.value.map(fieldConfig => {
        return {
          value: fieldConfig.id,
          label: fieldConfig.name,
          isCategory: fieldConfig.isCategory,
        }
      })
    })

    const fieldValues = ref({
      fieldNameOption: { label: '', value: '' },
      fieldValue: '',
    })

    const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
      TYPES.QueryRankingDomainService,
    )

    const queryRankingModal = useQueryRankingModal<
      AttributesQueryRankingModalAcceptEventPayload,
      BaseQueryRankingModalAcceptEventPayload
    >({ resetFields, buildAcceptEventPayload }, ctx)

    const hasConflictingItem = computed(() => {
      return qrDomainService.areAttributeRankingFieldsInConflict(props.queryRanking, {
        id: props.queryRankingConfig ? props.queryRankingConfig.id : undefined,
        fieldName: fieldValues.value.fieldNameOption.value,
        fieldValue: fieldValues.value.fieldValue,
        schedule: queryRankingModal.schedule.value,
      })
    })

    const isFormValid = computed((): boolean => {
      const isFieldNameOptionValid = !!fieldValues.value.fieldNameOption.value
      const isFieldValueValid = !!fieldValues.value.fieldValue

      return isFieldNameOptionValid && isFieldValueValid && !hasConflictingItem.value
    })

    function resetFields(): void {
      if (props.queryRankingConfig) {
        fieldValues.value = {
          fieldNameOption: fieldNameOptions.value.find(
            (epbSelectOption: EpbSelectOption) =>
              epbSelectOption.value === props.queryRankingConfig.fieldName,
          ) as { label: string; value: string },
          fieldValue: props.queryRankingConfig.fieldValue,
        }
      } else {
        fieldValues.value = {
          fieldNameOption: { label: '', value: '' },
          fieldValue: '',
        }
      }
    }

    const setFieldValue = event => {
      fieldValues.value = {
        fieldNameOption: { label: '', value: event.name },
        fieldValue: event.value,
      }
    }

    function buildAcceptEventPayload(
      acceptEventPayload: AcceptEventPayload,
    ): AttributesQueryRankingModalAcceptEventPayload {
      const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
        TYPES.QueryRankingDomainService,
      )
      return [
        qrDomainService.createElementRanking({
          fieldName: fieldValues.value.fieldNameOption.value,
          fieldValue: fieldValues.value.fieldValue,
          schedule: acceptEventPayload.schedule,
          action: acceptEventPayload.action,
        }),
      ]
    }

    return {
      baseClass: 'attributes-query-ranking-modal',
      ...queryRankingModal,
      fieldNameOptions,
      fieldValues,
      isFormValid,
      hasConflictingItem,
      setFieldValue,
    }
  },
})
